import { useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronLeft } from "lucide-react";

import { useCurrencies, useProfile, useTokens } from "@/hooks";
import type { Currency } from "@/interfaces/currency";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import SmoothScroll from "../SmoothScroll";
import ScrollToTop from "../ScrollToTop";
import {
  setCryptoCode,
  setFiatCurrencyId,
} from "@/store/slices/p2pMarketSlice";
import type { Token } from "@/interfaces/token";

const P2pLayout = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { theme } = useAppSelector((state) => state.ui);

  const { user } = useAppSelector((state) => state.auth);

  const userId = user?.id || "";

  const { data: currencies } = useCurrencies();
  const { data: profile } = useProfile(userId);

  const { data: tokenData } = useTokens(1, 1000);

  const tokens: Token[] = tokenData?.data ?? [];
  // const allTokens = tokensState.length ? tokensState : tokens;

  const { fiatCurrencyId, cryptoCode } = useAppSelector(
    (state) => state.p2pMarket,
  );

  useEffect(() => {
    if (!currencies?.length) return;
    if (fiatCurrencyId) return;

    const userFiat = currencies.find(
      (c) => c.id === profile?.setting?.currency?.id,
    );

    const usdFiat = currencies.find((c) => c.iso === "USD");

    const fallback = currencies[0];

    const selected = userFiat?.id ?? usdFiat?.id ?? fallback.id;

    if (selected) {
      dispatch(setFiatCurrencyId(selected));
    }
  }, [currencies, profile, fiatCurrencyId, dispatch]);

  useEffect(() => {
    if (!tokens?.length) return;

    // already set → do nothing
    if (cryptoCode) return;

    const usdToken = tokens.find((t) => t.iso === "USD");

    const fallback = tokens[0];

    const selected = usdToken?.code ?? fallback.code;

    if (selected) {
      dispatch(setCryptoCode(selected));
    }
  }, [tokens, cryptoCode, dispatch]);

  return (
    <div className="min-h-screen w-full flex flex-col">
      <ScrollToTop />
      <SmoothScroll />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-30 h-16 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto max-w-7xl h-full flex items-center justify-between px-1 sm:px-4 lg:px-6">
          {/* Left */}
          <div className="flex items-center gap-3">
            <button
              className="p-2 hover:bg-muted rounded-lg"
              onClick={() => navigate(-1)}
            >
              <ChevronLeft className="h-8 w-8 font-semibold" />
            </button>
          </div>

          {/* MIDDLE */}
          <h2 className="font-semibold">P2P</h2>

          {/* Right */}
          <div className="flex items-center gap-2">
            <div className="relative">
              <select
                value={fiatCurrencyId ?? ""}
                onChange={(e) => {
                  const selected = currencies?.find(
                    (c) => c.id === e.target.value,
                  );
                  if (selected) {
                    dispatch(setFiatCurrencyId(selected.id));
                  }
                }}
                className={`appearance-none px-4 py-2.5 pr-10 border rounded-lg text-sm focus:outline-none focus:border-primary cursor-pointer ${
                  theme === "dark"
                    ? "bg-muted border-border text-foreground"
                    : "bg-white border-gray-200 text-gray-900"
                }`}
              >
                {currencies?.map((currency: Currency) => (
                  <option key={currency.id} value={currency.id}>
                    {currency.iso} ({currency.symbol})
                  </option>
                ))}
              </select>
              <ChevronDown
                className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none ${theme === "dark" ? "text-muted-foreground" : "text-gray-500"}`}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
};

export default P2pLayout;
